import { useEffect, useState } from "react";

/**
 * Shared breakpoints for system-size responsive layout.
 * - phone / mobile: phones & small tablets
 * - tablet: tablets + narrow laptop windows (incl. large Display Size)
 * - desktopWide: keep Calendar mini-side + timeline side-by-side
 *
 * Older ad-hoc values (520/560/640/880/900/1180) in App.js are left alone
 * where they already work; new shell rules should use these constants.
 */
export const BP = {
  phone: 480,
  mobile: 768,
  tablet: 1024,
  /** Calendar side panel stays on at this width and above. */
  desktopWide: 1101,
};

function matches(query) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
}

/**
 * Roughly two thirds of this app's styling is inline `style={{...}}`, which no
 * media query can reach. This hook is the escape hatch for those spots; prefer a
 * CSS media query whenever the element has a class.
 */
export function useMediaQuery(query) {
  const [match, setMatch] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatch(e.matches);
    setMatch(mql.matches);

    // Safari only gained addEventListener on MediaQueryList in 14.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return match;
}

/** True on phones and small tablets (<= 768px). */
export function useIsMobile() {
  return useMediaQuery(`(max-width: ${BP.mobile}px)`);
}

/** True on phones only (<= 480px). */
export function useIsPhone() {
  return useMediaQuery(`(max-width: ${BP.phone}px)`);
}

/**
 * True below desktop layout (<= 1024px): use off-canvas nav drawer so content
 * gets full width on tablets and when OS Display Size shrinks CSS pixels.
 */
export function useIsTablet() {
  return useMediaQuery(`(max-width: ${BP.tablet}px)`);
}

/** True when Calendar should keep mini-cal side + timeline (wide systems). */
export function useIsDesktopWide() {
  return useMediaQuery(`(min-width: ${BP.desktopWide}px)`);
}
